import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';
import AdminLayout from './components/AdminLayout';
import DashboardPage from './pages/DashboardPage';
import CustomersPage from './pages/CustomersPage';
import ProductsPage from './pages/ProductsPage';
import CatalogProductsPage from './pages/CatalogProductsPage';
import BrandsPage from './pages/BrandsPage';
import CategoriesPage from './pages/CategoriesPage';
import PackagesPage from './pages/PackagesPage';
import InventoryPage from './pages/InventoryPage';
import SuppliersPage from './pages/SuppliersPage';
import QuotationsPage from './pages/QuotationsPage';
import InvoicesPage from './pages/InvoicesPage';
import FinancePage from './pages/FinancePage';
import StaffPage from './pages/StaffPage';
import ProjectsPage from './pages/ProjectsPage';
import WarrantyPage from './pages/WarrantyPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
// AI & Feature Pages
import AIAssistantPage from './pages/AIAssistantPage';
import AICameraPage from './pages/AICameraPage';
import AIRecommendPage from './pages/AIRecommendPage';
import AIConversationsPage from './pages/AIConversationsPage';
import BookingPage from './pages/BookingPage';
import CalendarPage from './pages/CalendarPage';
import GPSTrackingPage from './pages/GPSTrackingPage';
import AnalyticsPage from './pages/AnalyticsPage';
import FileManagementPage from './pages/FileManagementPage';
import NotificationsPage from './pages/NotificationsPage';
import CustomerPortalPage from './pages/CustomerPortalPage';
import MobileAppsPage from './pages/MobileAppsPage';
import { useAdminStore } from './store/adminStore';
import OrdersPage from './pages/OrdersPage';
import InvoicePrintPage from './pages/InvoicePrintPage';

/**
 * AuthGuard - protects all /admin/* routes.
 *
 * Strategy:
 * - Wait for Zustand persist hydration (localStorage is sync but Zustand wraps it
 *   in a Promise internally, so we must wait for the callback).
 * - Once hydrated, if NOT authenticated → save the intended path SYNCHRONOUSLY
 *   (inline, before rendering <Navigate>) so it is always captured before the
 *   component unmounts.
 * - If authenticated → render children.
 */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, sessionExpiry, logout, extendSession } = useAdminStore();
  const location = useLocation();

  // Initialize hydrated state. Zustand wraps even synchronous localStorage in a
  // Promise, so hasHydrated() returns false until the microtask queue drains.
  const [hydrated, setHydrated] = useState(() => useAdminStore.persist.hasHydrated());

  useEffect(() => {
    // Already hydrated — nothing to do.
    if (hydrated) return;
    // Subscribe before checking again to avoid a TOCTOU race.
    const unsub = useAdminStore.persist.onFinishHydration(() => setHydrated(true));
    // In case hydration finished between the useState initializer and this effect.
    if (useAdminStore.persist.hasHydrated()) setHydrated(true);
    return unsub;
  }, [hydrated]);

  // Session timeout checker
  const checkSession = useCallback(() => {
    if (sessionExpiry && Date.now() > sessionExpiry) {
      logout();
    }
  }, [sessionExpiry, logout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(checkSession, 60_000);
    checkSession(); // immediate check on mount / auth change
    return () => clearInterval(interval);
  }, [isAuthenticated, checkSession]);

  // Extend session on user activity
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleActivity = () => extendSession();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    events.forEach((e) => document.addEventListener(e, handleActivity, { passive: true }));
    return () => events.forEach((e) => document.removeEventListener(e, handleActivity));
  }, [isAuthenticated, extendSession]);

  // Show spinner while waiting for localStorage hydration.
  if (!hydrated) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-[#7FA86A] border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  // Not authenticated → save the attempted path SYNCHRONOUSLY (inline, before
  // <Navigate> renders and this component unmounts), then redirect to login.
  if (!isAuthenticated) {
    const attempted = location.pathname;
    // Only save meaningful admin paths — avoid saving the login page itself.
    if (attempted && attempted !== '/ktec-gate-access' && attempted !== '/admin') {
      localStorage.setItem('ktec_redirect_attempt', attempted);
    }
    return <Navigate to="/ktec-gate-access" replace />;
  }

  return <>{children}</>;
}

/**
 * AdminApp - handles all /admin/* routes.
 * The login page lives at /ktec-gate-access (in App.tsx) — NOT here.
 * Routes here use relative paths (relative to /admin/).
 */
export default function AdminApp() {
  return (
    <Routes>
      {/* Redirect /admin → /admin/dashboard */}
      <Route index element={<Navigate to="dashboard" replace />} />

      {/* All admin pages are wrapped in AuthGuard + AdminLayout */}
      <Route
        element={
          <AuthGuard>
            <AdminLayout />
          </AuthGuard>
        }
      >
        {/* Orders — Website Requests */}
        <Route path="orders" element={<OrdersPage />} />
        {/* Core */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="products" element={<ProductsPage />} />
        {/* Catalog Management */}
        <Route path="catalog" element={<CatalogProductsPage />} />
        <Route path="brands" element={<BrandsPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="packages" element={<PackagesPage />} />
        {/* Operations */}
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="warranty" element={<WarrantyPage />} />
        {/* Sales & Finance */}
        <Route path="quotations" element={<QuotationsPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="finance" element={<FinancePage />} />
        {/* Analytics */}
        <Route path="reports" element={<ReportsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        {/* AI Features */}
        <Route path="ai-assistant" element={<AIAssistantPage />} />
        <Route path="ai-camera" element={<AICameraPage />} />
        <Route path="ai-recommend" element={<AIRecommendPage />} />
        <Route path="ai-conversations" element={<AIConversationsPage />} />
        {/* Operations+ */}
        <Route path="bookings" element={<BookingPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="gps" element={<GPSTrackingPage />} />
        {/* Files & Comms */}
        <Route path="files" element={<FileManagementPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        {/* Portals */}
        <Route path="customer-portal" element={<CustomerPortalPage />} />
        <Route path="mobile-apps" element={<MobileAppsPage />} />
        {/* System */}
        <Route path="settings" element={<SettingsPage />} />
        {/* Invoice Print (outside AdminLayout so it renders full-page) */}
      </Route>

      {/* Invoice print page — full screen, outside AdminLayout */}
      <Route
        element={
          <AuthGuard>
            <></>
          </AuthGuard>
        }
      >
        <Route path="invoices/:id/print" element={<InvoicePrintPage />} />
      </Route>

      {/* Catch-all: unknown /admin/* paths → dashboard */}
      <Route path="*" element={<Navigate to="dashboard" replace />} />
    </Routes>
  );
}
